import { InvitationDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { useEffect, useState } from 'react';
import { Params, useNavigate, useParams } from 'react-router';
import { idpService } from 'services/api/identity-provider.service';
import { useCurrentOrgId } from 'shared-state/location/hook';
import jwtDecode from 'jwt-decode';

export interface IVisitorsEntryState {
    orgId?: string;
    linkActive: boolean;
    isLoadingDialogVisible: boolean;
    errorResponse: any;
}

interface IVisitorsEntryRouteParams extends Params {
    formattedOrgName: string;
    customPath: string;
    invitationId: string;
    slug: string;
}

export const useVisitorsEntryState: () => IVisitorsEntryState = () => {
    const routeHistory = useNavigate();
    const params = useParams<IVisitorsEntryRouteParams>();
    const customUrl = `${params.formattedOrgName}/${params.customPath}`;
    const invitationId = params.invitationId;
    const slug = params.slug;
    const queryToken = new URLSearchParams(window.location.search).get('token') || '';
    const [invitation, setInvitation] = useState<InvitationDto | undefined>();
    const [accessPass, setAccessPass] = useState<any>(null);
    const [orgId, setOrgId] = useCurrentOrgId();
    const [linkActive, setLinkActive] = useState(true);
    const [errorResponse, setErrorResponse] = useState(undefined);
    const [isLoadingDialogVisible, setIsLoadingDialogVisible] = useState(true);

    const fetchInvitation = async () => {
        if (slug) {
            return await idpService.showAccessPassBySlug(slug);
        }
        return await idpService.showInvitation(customUrl, invitationId);
    };

    const authenticateVisitor = async (id, visitorName = 'Campus Visitor') => {
        try {
            await idpService.authVisitor(visitorName, id);

            const accessToken = localStorage.getItem('ivAccessToken');
            if (!accessToken) throw new Error('No Token');

            const tokenPayload = jwtDecode<any>(accessToken);
            if (accessToken && tokenPayload) {
                const redirectVisitorToLandingPage = customUrl !== 'undefined/undefined' ? customUrl : id;
                routeHistory('/visitorLandingPage/' + redirectVisitorToLandingPage);
            }
        } catch (err) {
            throw new Error('Error Found: ' + err);
        }
    };

    const authenticateAccessPass = async (slug, visitorName = 'Campus Visitor') => {
        try {
            await idpService.authAccessPass(visitorName, slug);

            const accessToken = localStorage.getItem('ivAccessToken');
            if (!accessToken) throw new Error('No Token');

            const tokenPayload = jwtDecode<any>(accessToken);
            if (accessToken && tokenPayload) {
                routeHistory('/visitorLandingPage/access-pass/' + slug);
            } else {
                routeHistory(`/access-link-evaluation/${slug}/${queryToken}`);
                sessionStorage.setItem('tempOrgId', orgId);
                return;
            }
        } catch (err) {
            routeHistory(`/access-link-evaluation/${slug}/${queryToken}`);
            sessionStorage.setItem('tempOrgId', orgId);
            return;
        }
    };

    useEffect(() => {
        fetchInvitation()
            .then((res) => {
                if (slug && res.access_pass_id) {
                    setAccessPass(res);
                } else {
                    setInvitation(res);
                }
                setOrgId(res.orgId);
            })
            .catch((error) => {
                if (error.response.status === 404) {
                    routeHistory('/inserted-invalid-link');
                }
                setErrorResponse(error.response.data.error);
                setOrgId(error?.response?.data?.error?.orgId);
                setLinkActive(false);
            });
    }, [customUrl, slug]);

    useEffect(() => {
        if (!invitation && !accessPass) return;

        if (invitation && !accessPass) {
            const { invitationId } = invitation;
            linkActive && authenticateVisitor(invitationId);
        }
        if (accessPass && !invitation) {
            const { slug } = accessPass;
            linkActive && authenticateAccessPass(slug);
        }
    }, [invitation, accessPass]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoadingDialogVisible(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    return {
        orgId,
        linkActive,
        isLoadingDialogVisible,
        errorResponse
    };
};
